"use client";

import { useState, useEffect } from "react";
import { Trophy, Medal, Award, Star } from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  getLeaderboardData,
  type LeaderboardEntry,
} from "../../services/backend/api";
import { useProfileContext } from "../../context/ProfileContext";

const Leaderboard = () => {
  const navigate = useNavigate();
  const { selectedProfile } = useProfileContext();
  const [isVisible, setIsVisible] = useState(false);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [leaderboardData, setLeaderboardData] = useState<LeaderboardEntry[]>([]);

  const podiumData = leaderboardData.slice(0, 3);
  const topCollectibles = Math.max(
    ...leaderboardData.map((student) => student.collectibles),
    1
  );

  const secondPlace = podiumData[1] ?? null;
  const firstPlace = podiumData[0] ?? null;
  const thirdPlace = podiumData[2] ?? null;

  useEffect(() => {
    setIsVisible(true);

    const loadLeaderboard = async () => {
      const data = await getLeaderboardData(selectedProfile?.classId);
      setLeaderboardData(data.entries);
    };

    loadLeaderboard();
  }, [selectedProfile?.classId]);

  const getRankIcon = (rank: number) => {
    switch (rank) {
      case 1:
        return <Trophy className="w-6 h-6 text-yellow-500" />;
      case 2:
        return <Medal className="w-6 h-6 text-gray-400" />;
      case 3:
        return <Award className="w-6 h-6 text-amber-600" />;
      default:
        return <Star className="w-5 h-5 text-blue-500" />;
    }
  };

  const getPodiumHeight = (rank: number) => {
    switch (rank) {
      case 1:
        return "h-32";
      case 2:
        return "h-24";
      case 3:
        return "h-20";
      default:
        return "h-16";
    }
  };

  const getPodiumColor = (rank: number) => {
    switch (rank) {
      case 1:
        return "from-yellow-400 to-yellow-600";
      case 2:
        return "from-gray-300 to-gray-500";
      case 3:
        return "from-amber-400 to-amber-600";
      default:
        return "from-blue-400 to-blue-600";
    }
  };

  const getAvatar = (name: string) => {
    const colors = [
      "bg-gradient-to-br from-purple-400 to-pink-400",
      "bg-gradient-to-br from-blue-400 to-cyan-400",
      "bg-gradient-to-br from-green-400 to-emerald-400",
    ];
    const colorIndex = name.length % colors.length;
    return colors[colorIndex];
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-200 via-pink-200 to-purple-400 p-4 relative">
      
      

      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div
          className={`text-center mb-12 transform transition-all duration-1000 ${
            isVisible
              ? "translate-y-0 opacity-100"
              : "-translate-y-10 opacity-0"
          }`}
        >
          <div className="inline-flex items-center gap-3 mb-4">
            <div className="p-3 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-2xl shadow-lg">
              <Trophy className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-5xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent">
              Leaderboard
            </h1>
          </div>
          <p className="text-slate-600 text-lg">
            Keep practicing to climb the leaderboard!
          </p>
        </div>

        {/* Podium Section */}
        <div
          className={`mb-16 transform transition-all duration-1200 delay-300 ${
            isVisible ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"
          }`}
        >
          <div className="flex items-end justify-center gap-8 mb-8">
            {/* Second Place */}
            <div className="text-center transform hover:scale-105 transition-transform duration-300">
              <div
                className={`w-20 h-20 ${getAvatar(
                  secondPlace?.name ?? ""
                )} rounded-full flex items-center justify-center text-white font-bold text-xl mb-4 shadow-xl border-4 border-gray-300`}
              >
                😊
              </div>
              <h3 className="text-slate-800 font-semibold text-lg mb-2">
                {secondPlace?.name ?? "-"}
              </h3>
              <div
                className={`${getPodiumHeight(
                  2
                )} w-24 bg-gradient-to-t ${getPodiumColor(
                  2
                )} rounded-t-lg flex items-center justify-center shadow-xl`}
              >
                <span className="text-white font-bold text-2xl">2</span>
              </div>
              <div className="mt-2 text-sm text-slate-600">
                {(secondPlace?.collectibles ?? 0)} points
              </div>
            </div>

            {/* First Place */}
            <div className="text-center transform hover:scale-105 transition-transform duration-300">
              <div className="animate-bounce mb-2">
                <Trophy className="w-8 h-8 text-yellow-400 mx-auto" />
              </div>
              <div
                className={`w-24 h-24 ${getAvatar(
                  firstPlace?.name ?? ""
                )} rounded-full flex items-center justify-center text-white font-bold text-2xl mb-4 shadow-2xl border-4 border-yellow-400 animate-pulse`}
              >
                👑
              </div>
              <h3 className="text-slate-800 font-bold text-xl mb-2">
                {firstPlace?.name ?? "-"}
              </h3>
              <div
                className={`${getPodiumHeight(
                  1
                )} w-28 bg-gradient-to-t ${getPodiumColor(
                  1
                )} rounded-t-lg flex items-center justify-center shadow-2xl relative overflow-hidden`}
              >
                <span className="text-white font-bold text-3xl z-10">1</span>
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent transform -skew-x-12 animate-pulse"></div>
              </div>
              <div className="mt-2 text-sm text-yellow-300 font-semibold">
                {(firstPlace?.collectibles ?? 0)} points
              </div>
            </div>

            {/* Third Place */}
            <div className="text-center transform hover:scale-105 transition-transform duration-300">
              <div
                className={`w-20 h-20 ${getAvatar(
                  thirdPlace?.name ?? ""
                )} rounded-full flex items-center justify-center text-white font-bold text-xl mb-4 shadow-xl border-4 border-amber-600`}
              >
                😊
              </div>
              <h3 className="text-slate-800 font-semibold text-lg mb-2">
                {thirdPlace?.name ?? "-"}
              </h3>
              <div
                className={`${getPodiumHeight(
                  3
                )} w-24 bg-gradient-to-t ${getPodiumColor(
                  3
                )} rounded-t-lg flex items-center justify-center shadow-xl`}
              >
                <span className="text-white font-bold text-2xl">3</span>
              </div>
              <div className="mt-2 text-sm text-slate-600">
                {(thirdPlace?.collectibles ?? 0)} points
              </div>
            </div>
          </div>
        </div>

        {/* Leaderboard Table */}
        <div
          className={`bg-white/20 backdrop-blur-lg rounded-3xl shadow-2xl border border-white/30 overflow-hidden transform transition-all duration-1000 delay-600 ${
            isVisible ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"
          }`}
        >
          <div className="p-6">
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-200/50">
                    <th className="text-left py-4 px-2 text-slate-700 font-semibold">
                      Rank
                    </th>
                    <th className="text-left py-4 px-2 text-slate-700 font-semibold">
                      Student
                    </th>
                    <th className="text-left py-4 px-2 text-slate-700 font-semibold">
                      Collectibles
                    </th>
                    <th className="text-left py-4 px-2 text-slate-700 font-semibold">
                      Words
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {leaderboardData.map((student, index) => (
                    <tr
                      key={student.id}
                      className={`group transition-all duration-300 transform hover:scale-102 ${
                        hoveredIndex === index
                          ? "bg-white/40 shadow-lg"
                          : "hover:bg-white/30"
                      }`}
                      onMouseEnter={() => setHoveredIndex(index)}
                      onMouseLeave={() => setHoveredIndex(null)}
                      style={{
                        animationDelay: `${index * 100}ms`,
                        animation: isVisible
                          ? "slideInUp 0.6s ease-out forwards"
                          : "none",
                      }}
                    >
                      <td className="py-4 px-2">
                        <div className="flex items-center gap-3">
                          <div className="flex items-center justify-center w-8 h-8">
                            {getRankIcon(student.rank)}
                          </div>
                          <span className="text-slate-800 font-semibold">
                            {student.rank}
                          </span>
                        </div>
                      </td>
                      <td className="py-4 px-2">
                        <div className="flex items-center gap-3">
                          <div
                            className={`w-10 h-10 ${getAvatar(
                              student.name
                            )} rounded-full flex items-center justify-center text-white font-semibold shadow-lg group-hover:scale-110 transition-transform duration-300`}
                          >
                            😊
                          </div>
                          <span className="text-slate-800 font-medium">
                            {student.name}
                          </span>
                        </div>
                      </td>
                      <td className="py-4 px-2">
                        <div className="text-slate-800 font-semibold text-lg">
                          {student.collectibles}
                          <div className="w-full bg-white/20 rounded-full h-2 mt-1 max-w-24">
                            <div
                              className="bg-gradient-to-r from-blue-400 to-purple-500 h-2 rounded-full transition-all duration-1000"
                              style={{
                                width: `${Math.min(
                                  (student.collectibles / topCollectibles) * 100,
                                  100
                                )}%`,
                              }}
                            ></div>
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-2">
                        <span className="text-slate-700 font-medium">
                          {student.words}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards */}
            <div className="md:hidden space-y-3">
              {leaderboardData.map((student, index) => (
                <div
                  key={student.id}
                  className={`group p-4 rounded-2xl transition-all duration-300 transform hover:scale-102 ${
                    hoveredIndex === index
                      ? "bg-white/40 shadow-lg"
                      : "bg-white/20 hover:bg-white/30"
                  }`}
                  onMouseEnter={() => setHoveredIndex(index)}
                  onMouseLeave={() => setHoveredIndex(null)}
                  style={{
                    animationDelay: `${index * 100}ms`,
                    animation: isVisible
                      ? "slideInUp 0.6s ease-out forwards"
                      : "none",
                  }}
                >
                  {/* Mobile Header */}
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="flex items-center justify-center w-8 h-8">
                        {getRankIcon(student.rank)}
                      </div>
                      <span className="text-slate-800 font-semibold text-lg">
                        #{student.rank}
                      </span>
                    </div>
                  </div>

                  {/* Student Info */}
                  <div className="flex items-center gap-3 mb-3">
                    <div
                      className={`w-12 h-12 ${getAvatar(
                        student.name
                      )} rounded-full flex items-center justify-center text-white font-semibold shadow-lg group-hover:scale-110 transition-transform duration-300`}
                    >
                      😊
                    </div>
                    <div>
                      <div className="text-slate-800 font-medium text-lg">
                        {student.name}
                      </div>
                      <div className="text-slate-600 text-sm">
                        {student.words} words
                      </div>
                    </div>
                  </div>

                  {/* Progress */}
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-slate-700 text-sm font-medium">
                        Collectibles
                      </span>
                      <span className="text-slate-800 font-semibold">
                        {student.collectibles}/35
                      </span>
                    </div>
                    <div className="w-full bg-white/20 rounded-full h-3">
                      <div
                        className="bg-gradient-to-r from-blue-400 to-purple-500 h-3 rounded-full transition-all duration-1000"
                        style={{
                            width: `${Math.min(
                              (student.collectibles / topCollectibles) * 100,
                              100
                            )}%`,
                        }}
                      ></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Back button */}
        <div className="text-center mt-8">
          <button
            className="px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300"
            onClick={() => navigate("/profile")}
          >
            Back to Home
          </button>
        </div>

        {/* Footer Message */}
        <div
          className={`text-center mt-8 transform transition-all duration-1000 delay-1000 ${
            isVisible ? "translate-y-0 opacity-100" : "translate-y-5 opacity-0"
          }`}
        >
          <p className="text-slate-600 text-lg italic">
            Keep practicing to climb the leaderboard! 🚀
          </p>
        </div>
      </div>

      {/* Custom Animations */}
      <style>{`
        @keyframes slideInUp {
          from {
            opacity: 0;
            transform: translate3d(0, 100%, 0);
          }
          to {
            opacity: 1;
            transform: translate3d(0, 0, 0);
          }
        }
      `}</style>
    </div>
  );
};

export default Leaderboard;
